import React, { useState, useMemo, useEffect, memo } from "react"
import SelectionCard from "../SelectionCard"

import {
    ImprovedCartActionsEnum,
    ITicketGroup,
    IDayType,
} from "types/improved-booking"
import { useCartState, useCartDispatch } from "hooks/useCartV2"
import StepPlaceholderMessage from "../StepPlaceholderMessage"
import { useLang } from "hooks/useLang"
import useWindowSize from "hooks/useWindowSize"
import { AnimatePresence } from "framer-motion"
import CustomImage from "components/atoms/Image/CustomImage"
import SAR from "components/Sar/SAR"
import Sidebar from "components/bookingv2/Sidebar"
import { useTicketProcessing } from "hooks/useTicketProcessing"
import { calculateTicketAndGroupStatuses } from "utils/helpers/bookingv2/bookingHelpers"
import BottomSheetV2 from "components/atoms/BottomSheetV2"
import GallerySlider from "components/Slider/GallerySlider"
import { useBookingV2 } from "context/BookingV2Context"

const TICKET_IMAGES = [
    // "/images/guvali1.jpg",
    // "/images/guvali2.jpg",
    // "/images/guvali3.jpg",
    // "/images/guvali4.jpg",
    // "/images/guvali5.jpg",
]

const SelectTickets = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [selectedTicket, setSelectedTicket] = useState<any | null>(null)
    const { setIsBottomSheetOpen } = useBookingV2()

    // Sync local bottom sheet state with booking context so BookingLayout
    // can hide the action card while the description sheet is open on mobile.
    useEffect(() => {
        setIsBottomSheetOpen(isSidebarOpen)
    }, [isSidebarOpen, setIsBottomSheetOpen])
    const [validationMessages, setValidationMessages] = useState<
        Record<string, string>
    >({})
    const cartState = useCartState()
    const cartDispatch = useCartDispatch()
    const [selectedTicketTitle, setSelectedTicketTitle] = useState<string>("")
    const [selectedTicketPrice, setSelectedTicketPrice] = useState<number>(0)
    const [selectedTicketDescription, setSelectedTicketDescription] =
        useState<string>("")

    const { multiLangVars, t, dynamicTranslate } = useLang()
    const { isMobile } = useWindowSize()
    const [isImageClicked, setIsImageClicked] = useState({
        open: false,
        index: 0,
    })
    const selectedTimeslotId = cartState?.selectedDays?.timeslot_id

    // Use our custom hook for ticket processing
    const { processedTicketTypes, isTicketSoldOut, isTicketWaitlistAvailable } =
        useTicketProcessing()

    // Alias for backward compatibility
    const ticketTypesOfSelectedTimeslot = processedTicketTypes

    // Auth is moved to after ticket selection (BookingActionCard)

    const handleIncreaseClick = (
        e: React.MouseEvent,
        timeslotId: string,
        ticketData: IDayType
    ) => {
        e.stopPropagation()
        if (
            !cartState.selectedDays ||
            !cartState.selectedDays.start ||
            !cartState.selectedDays.end
        ) {
            return
        }

        const isSoldOutCalc =
            ticketData.limit === 0 || ticketData.limit < (ticketData.min || 1)
        const isStartWaitlistCalc =
            typeof ticketData.wait_list === "number" &&
            ticketData.wait_list > 0 &&
            isSoldOutCalc

        // Auth is no longer checked here; user can select waitlist tickets without authentication

        const currentTicketCount = cartState.selectedTickets.filter(
            (selectedTicket) =>
                selectedTicket.type_id === ticketData.type_id &&
                selectedTicket.timeslot_id === timeslotId
        ).length

        const limitValue = isStartWaitlistCalc
            ? ticketData.wait_list
            : ticketData.limit
        const maxAvailable =
            ticketData.max === 0
                ? limitValue
                : Math.min(ticketData.max, limitValue!)

        if (currentTicketCount < maxAvailable!) {
            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_TICKET,
                payload: {
                    type_id: ticketData.type_id,
                    timeslot_id: timeslotId,
                    ar_title: ticketData.ar_title,
                    en_title: ticketData.en_title,
                    start: cartState.selectedDays?.start,
                    end: cartState.selectedDays?.end,
                    price: ticketData.price,
                    min: ticketData.min || 1,
                    max: maxAvailable!,
                    limit: limitValue!,
                    color: ticketData.color,
                    must_select_seat: ticketData.must_select_seat,
                    promotion: ticketData.promotion || undefined,
                    group_id: ticketData.group?._id || undefined,
                    isWaitlist: isStartWaitlistCalc,
                    //TODO: fix
                    //@ts-ignore
                    label: ticketData.label,
                },
            })

            // Clear validation message when successfully adding
            setValidationMessages((prev) => ({
                ...prev,
                [ticketData.type_id]: "",
            }))
        } else {
            // Show max reached message
            setValidationMessages((prev) => ({
                ...prev,
                [ticketData.type_id]: `Maximum ${maxAvailable} tickets reached`,
            }))
        }
    }

    const handleDecreaseClick = (e: React.MouseEvent, ticketTypeId: string) => {
        e.stopPropagation()

        cartDispatch({
            type: ImprovedCartActionsEnum.REMOVE_TICKET,
            payload: { type_id: ticketTypeId },
        })
    }

    const handleCardClick = (ticket) => {
        if (selectedTicket && selectedTicket._id === ticket._id) {
            setIsSidebarOpen(false)
            setSelectedTicket(null)
        } else {
            setSelectedTicket(ticket)
            setIsSidebarOpen(true)
        }
    }

    // Auth modal handling removed from ticket selection step

    // Populate ticket information when a ticket is selected
    useEffect(() => {
        if (selectedTicket) {
            setSelectedTicketTitle(
                selectedTicket[
                    multiLangVars("title") as "en_title" | "ar_title"
                ] ||
                    selectedTicket.en_title ||
                    ""
            )
            setSelectedTicketPrice(selectedTicket.price || 0)
            setSelectedTicketDescription(
                selectedTicket[
                    multiLangVars("description") as
                        | "en_description"
                        | "ar_description"
                ] ||
                    selectedTicket.en_description ||
                    ""
            )
        }
    }, [selectedTicket, multiLangVars])

    // Login success handler removed (auth moved to BookingActionCard)

    const groupedTickets = useMemo(() => {
        const groups: Record<
            string,
            {
                group: ITicketGroup
                tickets: typeof ticketTypesOfSelectedTimeslot
            }
        > = {}

        ticketTypesOfSelectedTimeslot.forEach((ticketType) => {
            const groupKey = ticketType.group?._id || ""

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    group:
                        ticketType.group ||
                        ({
                            en_name: "",
                            ar_name: "",
                            _id: "",
                        } as ITicketGroup),
                    tickets: [],
                }
            }

            groups[groupKey].tickets.push(ticketType)
        })

        return Object.values(groups)
    }, [ticketTypesOfSelectedTimeslot])

    const sortedGroupedTickets = useMemo(() => {
        if (!groupedTickets) return []

        const groupsCopy = groupedTickets.map((group) => ({
            ...group,
            tickets: [...group.tickets],
        }))

        groupsCopy.sort((a, b) => {
            const isGroupASoldOut = a.tickets.every((ticket) => {
                const isSoldOut =
                    ticket.limit === 0 || ticket.limit < (ticket.min || 1)
                const hasWaitList =
                    typeof ticket.wait_list === "number" && ticket.wait_list > 0
                return isSoldOut && !hasWaitList
            })
            const isGroupBSoldOut = b.tickets.every((ticket) => {
                const isSoldOut =
                    ticket.limit === 0 || ticket.limit < (ticket.min || 1)
                const hasWaitList =
                    typeof ticket.wait_list === "number" && ticket.wait_list > 0
                return isSoldOut && !hasWaitList
            })

            if (isGroupASoldOut === isGroupBSoldOut) {
                return 0
            }
            return isGroupASoldOut ? 1 : -1
        })

        groupsCopy.forEach((group) => {
            group.tickets.sort((a, b) => {
                const isASoldOut =
                    (a.limit === 0 || a.limit < (a.min || 1)) &&
                    !(typeof a.wait_list === "number" && a.wait_list > 0)
                const isBSoldOut =
                    (b.limit === 0 || b.limit < (b.min || 1)) &&
                    !(typeof b.wait_list === "number" && b.wait_list > 0)

                if (isASoldOut === isBSoldOut) {
                    return 0
                }
                return isASoldOut ? 1 : -1
            })
        })

        return groupsCopy
    }, [groupedTickets])

    // Calculate statuses
    const { ticketTypeStatuses, groupStatuses } =
        calculateTicketAndGroupStatuses(
            ticketTypesOfSelectedTimeslot as IDayType[],
            cartState.selectedTickets || [],
            dynamicTranslate,
            selectedTimeslotId
        )

    // Validation messages for min/max ticket selection
    useEffect(() => {
        if (!selectedTimeslotId || !cartState.selectedTickets) return

        const newValidationMessages: Record<string, string> = {}

        ticketTypesOfSelectedTimeslot.forEach((ticketType) => {
            const currentTicketCount = cartState.selectedTickets.filter(
                (selectedTicket) =>
                    selectedTicket.type_id === ticketType.type_id &&
                    selectedTicket.timeslot_id === selectedTimeslotId
            ).length

            const isSoldOutCalc =
                ticketType.limit === 0 ||
                ticketType.limit < (ticketType.min || 1)
            const isStartWaitlistCalc =
                typeof ticketType.wait_list === "number" &&
                ticketType.wait_list > 0 &&
                isSoldOutCalc

            const limitValue = isStartWaitlistCalc
                ? ticketType.wait_list
                : ticketType.limit
            const maxAvailable =
                ticketType.max === 0
                    ? limitValue!
                    : Math.min(ticketType.max, limitValue!)
            // Only show validation messages if tickets are actually selected
            if (currentTicketCount > 0) {
                // Check minimum requirement
                if (currentTicketCount < (ticketType.min || 1)) {
                    const ticketNameForMessage =
                        (ticketType[
                            multiLangVars("title") as "en_title" | "ar_title"
                        ] as string) ||
                        ticketType.en_title ||
                        "This Ticket"
                    newValidationMessages[ticketType.type_id] =
                        dynamicTranslate("select_min_tickets_for_type", {
                            min: ticketType.min || 1,
                            ticketName: "",
                        })
                }
                // Check maximum reached
                else if (
                    currentTicketCount >= maxAvailable &&
                    maxAvailable > 0
                ) {
                    newValidationMessages[ticketType.type_id] =
                        `Maximum ${maxAvailable} tickets reached`
                }
                // Clear message if within valid range
                else {
                    newValidationMessages[ticketType.type_id] = ""
                }
            } else {
                // Clear message when no tickets are selected
                newValidationMessages[ticketType.type_id] = ""
            }
        })

        setValidationMessages(newValidationMessages)
    }, [
        cartState.selectedTickets,
        selectedTimeslotId,
        ticketTypesOfSelectedTimeslot,
    ])

    if (!selectedTimeslotId) {
        return <StepPlaceholderMessage message={t("select_day_time")} />
    } else if (
        ticketTypesOfSelectedTimeslot.length === 0 ||
        !ticketTypesOfSelectedTimeslot
    ) {
        return <StepPlaceholderMessage message={"no_tickets_available"} />
    }

    return (
        <div className="w-full relative mt-3">
            <div className="w-full">
                {sortedGroupedTickets.map((groupedTicket) => {
                    const groupStatusMessage =
                        groupStatuses[groupedTicket.group._id]
                            ?.groupStatusMessage
                    return (
                        <div
                            key={groupedTicket.group._id || ""}
                            className="flex flex-col gap-y-1 mb-5"
                        >
                            {groupedTicket.group._id !== "" && (
                                <div>
                                    <h6 className="text-lg lg:text-xl font-bold capitalize">
                                        {
                                            groupedTicket.group[
                                                multiLangVars("name") as
                                                    | "en_name"
                                                    | "ar_name"
                                            ]
                                        }
                                    </h6>
                                    {groupStatusMessage && (
                                        <p className="text-red-500 text-sm">
                                            {groupStatusMessage}
                                        </p>
                                    )}
                                </div>
                            )}
                            {groupedTicket.tickets.map((ticketType, index) => {
                                const ticketStatus =
                                    ticketTypeStatuses[ticketType.type_id]

                                const {
                                    isIncreaseDisabled,
                                    isDecreaseDisabled,
                                    ticketTypeStatusMessage,
                                    ticketTypeStatusColor,
                                } = ticketStatus

                                const currentTicketCount =
                                    cartState.selectedTickets.filter(
                                        (selectedTicket) =>
                                            selectedTicket.type_id ===
                                                ticketType.type_id &&
                                            selectedTicket.timeslot_id ===
                                                selectedTimeslotId
                                    ).length

                                const isSoldOut = isTicketSoldOut(ticketType)
                                const isStartWaitlist =
                                    isTicketWaitlistAvailable(ticketType)

                                // If waitlist is available, ticket is not sold out
                                const finalIsSoldOut = isStartWaitlist
                                    ? false
                                    : isSoldOut

                                return (
                                    <SelectionCard
                                        variant="ticket"
                                        key={ticketType._id}
                                        title={
                                            ticketType[
                                                multiLangVars("title") as
                                                    | "en_title"
                                                    | "ar_title"
                                            ]
                                        }
                                        price={ticketType.price}
                                        isFirst={index === 0}
                                        isLast={
                                            index ===
                                            groupedTicket.tickets.length - 1
                                        }
                                        quantity={currentTicketCount}
                                        onIncrease={(e) =>
                                            !isIncreaseDisabled &&
                                            handleIncreaseClick(
                                                e,
                                                selectedTimeslotId!,
                                                ticketType
                                            )
                                        }
                                        onDecrease={(e) =>
                                            !isDecreaseDisabled &&
                                            handleDecreaseClick(
                                                e,
                                                ticketType.type_id
                                            )
                                        }
                                        increaseDisabled={isIncreaseDisabled}
                                        decreaseDisabled={isDecreaseDisabled}
                                        statusMessage={ticketTypeStatusMessage}
                                        statusColor={ticketTypeStatusColor}
                                        validationMessage={
                                            validationMessages[
                                                ticketType.type_id
                                            ] || ""
                                        }
                                        onClick={() =>
                                            handleCardClick(ticketType)
                                        }
                                        discountPreviousValue={
                                            ticketType.discount?.previous_value
                                        }
                                        hasOffer={!!ticketType.promotion}
                                        offerText={
                                            (ticketType.promotion?.[
                                                multiLangVars(
                                                    "title"
                                                ) as keyof typeof ticketType.promotion
                                            ] as string) ||
                                            ticketType.promotion?.en_title
                                        }
                                        isSoldOut={finalIsSoldOut}
                                        isStartWaitlist={isStartWaitlist}
                                        label={ticketType.label}
                                    />
                                )
                            })}
                        </div>
                    )
                })}

                <div className="hidden md:block">
                    <Sidebar
                        isOpen={isSidebarOpen}
                        onClose={
                            !isMobile
                                ? () => setIsSidebarOpen(false)
                                : () => {
                                      /* No operation */
                                  }
                        }
                        title={selectedTicketTitle || ""}
                    >
                        <div className="flex flex-col gap-y-5">
                            <div className="flex items-start">
                                <div className="font-bold text-xl">
                                    <SAR
                                        color={"#201750"}
                                        price={selectedTicketPrice}
                                    />
                                </div>
                            </div>
                            {/* Auth modal removed from ticket selection step */}
                            <div className="flex flex-col gap-y-2">
                                <h4 className="font-bold text-xl capatilize">
                                    {t("description")}
                                </h4>
                                <p className="text-sm">
                                    {selectedTicketDescription}
                                </p>
                            </div>

                            {TICKET_IMAGES.length > 0 && (
                                <div className="images flex flex-col gap-y-2">
                                    <h4 className="font-bold text-xl ">
                                        {t("images")}
                                    </h4>
                                    <div className="flex flex-wrap gap-y-3 gap-x-5">
                                        {TICKET_IMAGES?.map(
                                            (poster: string, i: number) => (
                                                <div
                                                    key={i}
                                                    onClick={() =>
                                                        setIsImageClicked({
                                                            open: true,
                                                            index: i,
                                                        })
                                                    }
                                                    className="relative h-24 cursor-pointer rounded-md aspect-video"
                                                >
                                                    <CustomImage
                                                        className="rounded-md"
                                                        key={poster}
                                                        src={TICKET_IMAGES[i]}
                                                        alt={`mini-img-${poster}`}
                                                        fill
                                                        style={{
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Sidebar>
                </div>

                <div className="block md:hidden">
                    <BottomSheetV2
                        className={`${TICKET_IMAGES.length > 0 ? "h-[70vh]" : ""}`}
                        isOpen={isSidebarOpen}
                        onClose={
                            isMobile
                                ? () => setIsSidebarOpen(false)
                                : () => {
                                      /* No operation */
                                  }
                        }
                        title={selectedTicketTitle || ""}
                    >
                        <div className="flex flex-col gap-y-5 h-full w-full items-start">
                            <div className="font-bold text-xl">
                                <SAR
                                    color={"#201750"}
                                    price={selectedTicketPrice}
                                />
                            </div>

                            <div className="flex flex-col ">
                                <h4 className="font-bold text-xl capatilize">
                                    {t("description")}
                                </h4>
                                <p className="text-sm">
                                    {selectedTicketDescription}
                                </p>
                            </div>

                            {TICKET_IMAGES.length > 0 && (
                                <div className="images flex flex-col">
                                    <h4 className="font-bold text-xl">
                                        {t("images")}
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2 pt-4 md:grid-cols-3 lg:md:grid-cols-4 mb-3">
                                        {TICKET_IMAGES.map(
                                            (poster: string, i: number) => (
                                                <div
                                                    key={i}
                                                    onClick={() =>
                                                        setIsImageClicked({
                                                            open: true,
                                                            index: i,
                                                        })
                                                    }
                                                    className="relative h-24 aspect-video w-full cursor-pointer rounded-md"
                                                >
                                                    <CustomImage
                                                        className="rounded-md"
                                                        key={poster}
                                                        src={TICKET_IMAGES[i]}
                                                        alt={`mini-img-${poster}`}
                                                        fill
                                                        style={{
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </BottomSheetV2>
                </div>

                <AnimatePresence>
                    {isImageClicked.open ? (
                        <GallerySlider
                            images={TICKET_IMAGES}
                            clickedIndex={isImageClicked.index}
                            close={() =>
                                setIsImageClicked({
                                    open: false,
                                    index: 0,
                                })
                            }
                        />
                    ) : null}
                </AnimatePresence>
            </div>

            {/* Auth modal removed from ticket selection step */}
        </div>
    )
}

export default memo(SelectTickets)
